import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, MapPin, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const params = new URLSearchParams(useLocation().search);
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null });
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const data = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          clear();
          setState({ status: "paid", data });
          return;
        }
        if (data.payment_status === "expired" || data.status === "failed") {
          setState({ status: "failed", data });
          return;
        }
        if (attempts >= 10) {
          setState({ status: "timeout", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 10) return setState({ status: "error", data: null });
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="min-h-screen island-pattern">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-white rounded-3xl border border-[#E5D9C5] shadow-[0_20px_60px_rgba(44,36,27,0.08)] p-8 slide-up" data-testid="payment-status-card">
          {state.status === "loading" && (
            <div className="text-center" data-testid="payment-loading">
              <Loader2 className="w-14 h-14 mx-auto text-[#D9381E] animate-spin" />
              <h1 className="mt-4 font-display font-extrabold text-2xl">Confirming your payment…</h1>
              <p className="mt-2 text-[#7A6C5D]">Please hold on, this takes just a moment.</p>
            </div>
          )}

          {state.status === "paid" && state.data?.order && (
            <div data-testid="payment-success">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#2C5E3B] flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h1 className="mt-5 font-display font-extrabold text-3xl text-[#2C241B] text-center">Order confirmed!</h1>
              <p className="mt-2 text-[#7A6C5D] text-center">Thanks {state.data.order.customer_name.split(" ")[0]}, we'll have your food ready.</p>

              <OrderDetails order={state.data.order} />

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/" data-testid="back-home-btn" className="cta-primary rounded-full px-6 py-3 font-display font-extrabold text-sm text-center">Order More</Link>
              </div>
            </div>
          )}

          {(state.status === "failed" || state.status === "error") && (
            <div className="text-center" data-testid="payment-failed">
              <XCircle className="w-14 h-14 mx-auto text-[#D9381E]" />
              <h1 className="mt-4 font-display font-extrabold text-2xl">Payment not completed</h1>
              <p className="mt-2 text-[#7A6C5D]">We couldn't confirm your payment. Please try again.</p>
              <Link to="/" className="mt-6 inline-block cta-primary rounded-full px-6 py-3 font-display font-extrabold text-sm">Back to menu</Link>
            </div>
          )}

          {state.status === "timeout" && (
            <div className="text-center" data-testid="payment-timeout">
              <Loader2 className="w-14 h-14 mx-auto text-[#D9381E]" />
              <h1 className="mt-4 font-display font-extrabold text-2xl">Still processing…</h1>
              <p className="mt-2 text-[#7A6C5D]">Your payment is taking longer than expected. Please refresh in a minute.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function OrderDetails({ order }) {
  return (
    <div className="mt-6 bg-[#F9F6F0] rounded-2xl p-5 border border-[#E5D9C5]" data-testid="order-details">
      <div className="text-xs text-[#7A6C5D]">Order #{order.id.slice(0, 8).toUpperCase()}</div>
      <div className="mt-4 space-y-2">
        {order.items.map(it => (
          <div key={it.id} className="flex justify-between text-sm">
            <span>{it.quantity} × {it.name}</span>
            <span className="font-semibold">${it.line_total.toFixed(2)}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-[#E5D9C5] flex justify-between items-baseline">
        <span className="font-display font-extrabold">Total</span>
        <span className="font-display font-extrabold text-xl text-[#D9381E]">${order.total.toFixed(2)}</span>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="flex items-start gap-2">
          <Clock className="w-4 h-4 mt-0.5 text-[#2C5E3B]" />
          <div>
            <div className="font-semibold">Pickup</div>
            <div className="text-[#7A6C5D]">{order.pickup_date} · {order.pickup_slot}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-0.5 text-[#2C5E3B]" />
          <div>
            <div className="font-semibold">Location</div>
            <div className="text-[#7A6C5D]">10508 S 54th Lane, Laveen, AZ 85339</div>
          </div>
        </div>
      </div>
      <div className="mt-4 text-xs text-[#7A6C5D]">
        Payment: <span className="font-semibold text-[#2C241B]">{order.payment_status === "paid" ? "Paid online" : "Pay at pickup"}</span>
      </div>
    </div>
  );
}
